import { Reducer, Dispatch } from "redux";
import * as moment from "moment";
import {
    ModuleInformation,
    CalendarState,
    TextInformation,
    PersonalDatePresentationItem,
    OptionalDaySettings,
    ChangeCalendarData,
    ExtraPageInfo,
    OwnerPageInfo,
    MonthDividerInfo,
} from "burde";
import { ApplicationState } from "store";
import {
    CalendarType,
    A5CalendarNumberOfExtraPages,
    MonthDividerNumberOfPages,
} from "svg/constants";
import { actionCreators as uiActionCreators } from "ui/store";
import {
    DefaultSize,
    DefaultCulture,
    DefaultCoverTextCalendarA5,
} from "ui/constants";
import { getCalendarMaxNumberOfExtraPages } from "./helper";

export enum ActionType {
    UpdateStartDate = "UPDATE_START_DATE",
    UpdatePersonalDateItems = "UPDATE_PERSONAL_DATE_PRESENTATION_ITEM",
    AddWeekModule = "ADD_WEEK_MODULE",
    RemoveWeekModule = "REMOVE_WEEK_MODULE",
    AddDayModule = "ADD_DAY_MODULE",
    RemoveDayModule = "REMOVE_DAY_MODULE",
    ChangeFontSet = "CHANGE_FONTSET",
    ChangeCalendar = "CHANGE_CALENDAR",
    UpdateEndPageCollection = "UPDATE_END_PAGE_COLLECTION",
    UpdateLines = "UPDATE_LINES",
    UpdateOptionalDaySettings = "UPDATE_OPTIONAL_DAY_SETTINGS",
    UpdateCalendarSize = "UPDATE_CALENDAR_SIZE",
    UpdateCalendarCulture = "UPDATE_CALENDAR_CULTURE",
    UpdateLayoutId = "UPDATE_LAYOUT_ID",
    UpdateBorderColor = "UPDATE_BORDER_COLOR",
    UpdateBorderImageId = "UPDATE_BORDER_IMAGE_ID",
    UpdateExtraPageCollection = "UPDATE_EXTRA_PAGE_COLLECTION",
    RemoveExtraPageFromCollection = "REMOVE_EXTRA_PAGE_FROM_COLLECTION",
    UpdateMonthDivider = "UPDATE_MONTH_DIVIDER",
    UpdateOwnerPage = "UPDATE_OWNER_PAGE",
    UpdateIsTheme = "UPDATE_IS_THEME",
    UpdateCalendarType = "UPDATE_CALENDAR_TYPE",
    UpdateCustomNumberOfEndPages = "UPDATE_CUSTOM_NUMBER_OF_END_PAGES",
    UpdateCoverImageId = "UPDATE_COVER_IMAGE_ID",
    UpdateCustomCoverImageId = "UPDATE_CUSTOM_COVER_IMAGE_ID",
    UpdateCoverColor = "UPDATE_COVER_COLOR",
    UpdateBackCoverImageId = "UPDATE_BACK_COVER_IMAGE_ID",
    UpdateCustomBackImageId = "UPDATE_CUSTOM_BACK_IMAGE_ID",
    UpdateBackCoverColor = "UPDATE_BACK_COVER_COLOR",
    UpdateSpiral = "UPDATE_SPIRAL",
    UpdateRubberband = "UPDATE_RUBBERBAND",
    UpdateCoverTextCollection = "ADD_COVER_TEXT_COLLECTION",
    UpdateFontId = "UPDATE_FONT_ID",
}

interface UpdateStartDateAction {
    type: ActionType.UpdateStartDate;
    startDate: string;
}

interface UpdatePersonalDateItemsAction {
    type: ActionType.UpdatePersonalDateItems;
    personalDateItems: Array<PersonalDatePresentationItem>;
}

interface UpdateLayoutIdAction {
    type: ActionType.UpdateLayoutId;
    layoutId: number | null;
}

interface AddWeekModuleAction {
    type: ActionType.AddWeekModule;
    module: ModuleInformation;
}

interface RemoveWeekModuleAction {
    type: ActionType.RemoveWeekModule;
    slotId: number;
}

interface AddDayModuleAction {
    type: ActionType.AddDayModule;
    module: ModuleInformation;
}

interface RemoveDayModuleAction {
    type: ActionType.RemoveDayModule;
    slotId: number;
}

interface ChangeCalendarAction {
    type: ActionType.ChangeCalendar;
    changeCalendarData?: ChangeCalendarData;
}

interface UpdateBorderColorAction {
    type: ActionType.UpdateBorderColor;
    color: string;
}

interface UpdateBorderImageIdAction {
    type: ActionType.UpdateBorderImageId;
    borderImageId: number | null;
}

interface UpdateCoverTextCollectionAction {
    type: ActionType.UpdateCoverTextCollection;
    collection: Array<TextInformation>;
}

interface UpdateEndPageCollectionAction {
    type: ActionType.UpdateEndPageCollection;
    value: string;
}

interface UpdateLinesAction {
    type: ActionType.UpdateLines;
    value: string;
}

interface UpdateOptionalDaySettingsAction {
    type: ActionType.UpdateOptionalDaySettings;
    value: OptionalDaySettings;
}

interface UpdateCalendarSizeAction {
    type: ActionType.UpdateCalendarSize;
    value: string;
}

interface UpdateCalendarCultureAction {
    type: ActionType.UpdateCalendarCulture;
    value: string;
}

interface UpdateExtraPageCollection {
    type: ActionType.UpdateExtraPageCollection;
    value: number[];
}

interface RemoveExtraPageFromCollection {
    type: ActionType.RemoveExtraPageFromCollection;
    value: ExtraPageInfo;
    index: number;
}

interface UpdateMonthDividerAction {
    type: ActionType.UpdateMonthDivider;
    monthDivider: MonthDividerInfo;
}
interface UpdateOwnerPageAction {
    type: ActionType.UpdateOwnerPage;
    ownerPage: OwnerPageInfo;
}

interface UpdateIsThemeAction {
    type: ActionType.UpdateIsTheme;
    isTheme: boolean;
}

interface UpdateCalendarTypeAction {
    type: ActionType.UpdateCalendarType;
    calendarType: CalendarType;
}

interface UpdateCustomNumberOfEndPagesAction {
    type: ActionType.UpdateCustomNumberOfEndPages;
    customNumberOfEndPages: number;
}

interface UpdateCoverImageIdAction {
    type: ActionType.UpdateCoverImageId;
    imageId: number | null;
}

interface UpdateCustomCoverImageIdAction {
    type: ActionType.UpdateCustomCoverImageId;
    imageId: number | null;
}

interface UpdateCoverColorAction {
    type: ActionType.UpdateCoverColor;
    color: string;
}

interface UpdateBackCoverImageIdAction {
    type: ActionType.UpdateBackCoverImageId;
    imageId: number | null;
}

interface UpdateCustomBackImageIdAction {
    type: ActionType.UpdateCustomBackImageId;
    imageId: number | null;
}

interface UpdateBackCoverColorAction {
    type: ActionType.UpdateBackCoverColor;
    color: string;
}

interface UpdateSpiralAction {
    type: ActionType.UpdateSpiral;
    spiral: string;
}

interface UpdateRubberbandAction {
    type: ActionType.UpdateRubberband;
    rubberband: string;
}

interface UpdateFontIdAction {
    type: ActionType.UpdateFontId;
    fontId: number | null;
}

type CalendarAction =
    | UpdateStartDateAction
    | UpdatePersonalDateItemsAction
    | AddWeekModuleAction
    | RemoveWeekModuleAction
    | AddDayModuleAction
    | RemoveDayModuleAction
    | ChangeCalendarAction
    | UpdateCoverTextCollectionAction
    | UpdateEndPageCollectionAction
    | UpdateLinesAction
    | UpdateOptionalDaySettingsAction
    | UpdateCalendarSizeAction
    | UpdateCalendarCultureAction
    | UpdateLayoutIdAction
    | UpdateBorderColorAction
    | UpdateBorderImageIdAction
    | UpdateExtraPageCollection
    | RemoveExtraPageFromCollection
    | UpdateMonthDividerAction
    | UpdateOwnerPageAction
    | UpdateIsThemeAction
    | UpdateCalendarTypeAction
    | UpdateCustomNumberOfEndPagesAction
    | UpdateCoverImageIdAction
    | UpdateCustomCoverImageIdAction
    | UpdateCoverColorAction
    | UpdateBackCoverImageIdAction
    | UpdateCustomBackImageIdAction
    | UpdateBackCoverColorAction
    | UpdateSpiralAction
    | UpdateRubberbandAction
    | UpdateFontIdAction;

export const actionCreators = {
    updateStartDate: (startDate: string) =>
        <UpdateStartDateAction>{
            type: ActionType.UpdateStartDate,
            startDate,
        },
    addWeekModule: (module: ModuleInformation) =>
        <AddWeekModuleAction>{
            type: ActionType.AddWeekModule,
            module,
        },
    removeWeekModule: (slotId: number) =>
        <RemoveWeekModuleAction>{
            type: ActionType.RemoveWeekModule,
            slotId,
        },
    addDayModule: (module: ModuleInformation) =>
        <AddDayModuleAction>{
            type: ActionType.AddDayModule,
            module,
        },
    removeDayModule: (slotId: number) =>
        <RemoveDayModuleAction>{
            type: ActionType.RemoveDayModule,
            slotId,
        },
    updateBorderColor: (color: string | null) =>
        <UpdateBorderColorAction>{
            type: ActionType.UpdateBorderColor,
            color,
        },
    updateBorderImageId: (borderImageId: number | null) =>
        <UpdateBorderImageIdAction>{
            type: ActionType.UpdateBorderImageId,
            borderImageId,
        },
    updateCoverTextCollection: (collection: Array<TextInformation>) =>
        <UpdateCoverTextCollectionAction>{
            type: ActionType.UpdateCoverTextCollection,
            collection,
        },
    updateEndPageCollection: (value: string) =>
        <UpdateEndPageCollectionAction>{
            type: ActionType.UpdateEndPageCollection,
            value,
        },
    updateLines: (value: string) =>
        <UpdateLinesAction>{
            type: ActionType.UpdateLines,
            value,
        },
    updateCalendarCulture: (value: string) =>
        <UpdateCalendarCultureAction>{
            type: ActionType.UpdateCalendarCulture,
            value,
        },
    updateExtraPageCollection: (value: number[]) =>
        <UpdateExtraPageCollection>{
            type: ActionType.UpdateExtraPageCollection,
            value,
        },
    removeExtraPageFromCollection: (value: ExtraPageInfo, index: number) =>
        <RemoveExtraPageFromCollection>{
            type: ActionType.RemoveExtraPageFromCollection,
            value,
            index,
        },
    setActiveCalendar: (calendar?: CalendarState) => async (
        dispatch: Dispatch,
    ) => {
        const changeCalendarData = {
            calendar,
            loadedFromLocalStorage: false,
        };

        dispatch({
            type: ActionType.ChangeCalendar,
            changeCalendarData,
        });

        dispatch(uiActionCreators.UpdateEditableCustomImage(""));
        dispatch(uiActionCreators.UpdateEditableCustomBackImage(""));
        dispatch(uiActionCreators.UpdateLoadFromLocalStorage(false));
    },
    setCalendarFromStorage: (calendar?: CalendarState) => async (
        dispatch: Dispatch,
    ) => {
        const changeCalendarData = {
            calendar,
            loadedFromLocalStorage: true,
        };

        dispatch({
            type: ActionType.ChangeCalendar,
            changeCalendarData,
        });

        dispatch(uiActionCreators.UpdateEditableCustomImage(""));
        dispatch(uiActionCreators.UpdateEditableCustomBackImage(""));
        dispatch(uiActionCreators.UpdateLoadFromLocalStorage(false));
    },
    updateOptionalDaySettings: (value: OptionalDaySettings) =>
        <UpdateOptionalDaySettingsAction>{
            type: ActionType.UpdateOptionalDaySettings,
            value,
        },
    updateCalendarSize: (value: string) =>
        <UpdateCalendarSizeAction>{
            type: ActionType.UpdateCalendarSize,
            value,
        },
    updateLayoutId: (layoutId: number | null) =>
        <UpdateLayoutIdAction>{
            type: ActionType.UpdateLayoutId,
            layoutId,
        },
    updateMonthDivider: (monthDivider: MonthDividerInfo | null) =>
        <UpdateMonthDividerAction>{
            type: ActionType.UpdateMonthDivider,
            monthDivider,
        },
    updateOwnerPage: (ownerPage: OwnerPageInfo) =>
        <UpdateOwnerPageAction>{
            type: ActionType.UpdateOwnerPage,
            ownerPage,
        },
    updateIsTheme: (isTheme: boolean) =>
        <UpdateIsThemeAction>{
            type: ActionType.UpdateIsTheme,
            isTheme,
        },
    updateCalendarType: (calendarType: CalendarType) =>
        <UpdateCalendarTypeAction>{
            type: ActionType.UpdateCalendarType,
            calendarType: calendarType,
        },
    updateCustomNumberOfEndPages: (customNumberOfEndPages: number) =>
        <UpdateCustomNumberOfEndPagesAction>{
            type: ActionType.UpdateCustomNumberOfEndPages,
            customNumberOfEndPages: customNumberOfEndPages,
        },
    updateCoverImageId: (imageId: number | null) =>
        <UpdateCoverImageIdAction>{
            type: ActionType.UpdateCoverImageId,
            imageId: imageId,
        },
    updateCustomCoverImageId: (imageId: number | null) =>
        <UpdateCustomCoverImageIdAction>{
            type: ActionType.UpdateCustomCoverImageId,
            imageId: imageId,
        },
    updateCoverColor: (color: string | null) =>
        <UpdateCoverColorAction>{
            type: ActionType.UpdateCoverColor,
            color: color,
        },
    updateBackCoverImageId: (imageId: number | null) =>
        <UpdateBackCoverImageIdAction>{
            type: ActionType.UpdateBackCoverImageId,
            imageId: imageId,
        },
    updateCustomBackImageId: (imageId: number | null) =>
        <UpdateCustomBackImageIdAction>{
            type: ActionType.UpdateCustomBackImageId,
            imageId: imageId,
        },
    updateBackCoverColor: (color: string | null) =>
        <UpdateBackCoverColorAction>{
            type: ActionType.UpdateBackCoverColor,
            color: color,
        },
    updateSpiral: (spiral: string) =>
        <UpdateSpiralAction>{
            type: ActionType.UpdateSpiral,
            spiral,
        },
    updateRubberband: (rubberband: string) =>
        <UpdateRubberbandAction>{
            type: ActionType.UpdateRubberband,
            rubberband,
        },
    updateFontId: (fontId: number | null) =>
        <UpdateFontIdAction>{
            type: ActionType.UpdateFontId,
            fontId,
        },
};

export const updateStartDate = (startDate: string) => async (
    dispatch: Dispatch,
) => {
    dispatch({
        type: ActionType.UpdateStartDate,
        startDate: startDate,
    });
};

export const updateLayoutId = (layoutId: number | null) => async (
    dispatch: Dispatch,
) => {
    dispatch({
        type: ActionType.UpdateLayoutId,
        layoutId: layoutId,
    });
};

export const updateCalendarSize = (calendarSize: string) => async (
    dispatch: Dispatch,
) => {
    if (!calendarSize || calendarSize === "") {
        return;
    }

    dispatch({
        type: ActionType.UpdateCalendarSize,
        calendarSize: calendarSize,
    });
};

export const updatePersonalDateInfo = (
    personalDates: Array<PersonalDatePresentationItem>,
) => async (dispatch: Dispatch) => {
    dispatch({
        type: ActionType.UpdatePersonalDateItems,
        personalDateItems: personalDates,
    });
};

export const updateFontId = (fontId: number | null) => async (
    dispatch: Dispatch,
) => {
    dispatch({
        type: ActionType.UpdateFontId,
        fontId,
    });
};

export const getStartDate = ({ calendar }: ApplicationState): string =>
    calendar.startDate;

export const getModules = ({ calendar }: ApplicationState) =>
    calendar.weekModules;

export const getBorderImageId = (state: ApplicationState): number | null =>
    state.calendar.borderImageId;

export const getBorderColor = (state: ApplicationState): string | null =>
    state.calendar.borderColor;

export const getCalendar = (state: ApplicationState): CalendarState =>
    state.calendar;

export const getLayoutId = (state: ApplicationState) => state.calendar.layoutId;

export const getCalendarSize = ({ calendar }: ApplicationState): string =>
    calendar.calendarSize;

export const getCulture = ({ calendar }: ApplicationState): string =>
    calendar.culture != null ? calendar.culture : DefaultCulture;

export const getOptionalDaySettings = (state: ApplicationState) =>
    state.calendar.optionalDaySettings;

export const getPersonalDateItems = (state: ApplicationState) =>
    state.calendar.personalDateItems;

export const getAddedExtraPages = (state: ApplicationState) =>
    state.calendar.extraPages;

export const getOwnerPage = ({ calendar }: ApplicationState): OwnerPageInfo =>
    calendar.ownerPage;

export const getIsTheme = (state: ApplicationState): boolean =>
    state.calendar.isTheme;

export const getCalendarType = ({ calendar }: ApplicationState): string =>
    calendar.calendarType;

export const getCustomNumberOfEndPages = ({
    calendar,
}: ApplicationState): number => calendar.customNumberOfEndPages;

export const getCoverImageId = ({
    calendar,
}: ApplicationState): number | null => calendar.coverImageId;

export const getCustomCoverImageId = ({
    calendar,
}: ApplicationState): number | null => calendar.customCoverImageId;

export const getCoverColor = ({ calendar }: ApplicationState): string | null =>
    calendar.coverColor;

export const getBackCoverImageId = ({
    calendar,
}: ApplicationState): number | null => calendar.backCoverImageId;

export const getCustomBackImageId = ({
    calendar,
}: ApplicationState): number | null => calendar.customBackImageId;

export const getBackCoverColor = ({
    calendar,
}: ApplicationState): string | null => calendar.backCoverColor;

export const getFontId = ({ calendar }: ApplicationState): number | null =>
    calendar.fontId;

// Note (JH): Changed to function to prevent buildtime hoisting
const generateStartDate = () => {
    // Note (JH): Burde wants to set the start date to january during a few months at the end of each year.
    return moment().add(1, "months").startOf("month");
    // return moment("2024-01-01");
};

var initialStartDate = generateStartDate();

const initialOwnerPage = {
    id: 0,
    layoutId: 0,
    customImageId: null,
    imageId: null,
    email: "",
    name: "",
    phoneNumber: "",
    layout: null,
    isGift: false,
    greeting: "",
};

const initialState: CalendarState = {
    id: 0,
    name: "",
    weekModules: [],
    dayModules: [],
    startDate: initialStartDate.format("YYYY-MM-DD"),
    personalDateItems: [],
    optionalDaySettings: {
        showFlagDays: true,
        showMoonPhases: true,
        showNameDays: true,
        showOtherDays: true,
        showRedDays: true,
        showTimestamps: false,
    },
    thumbnailId: null,
    culture: DefaultCulture,
    calendarSize: DefaultSize,
    layoutId: null,
    userId: "",
    lines: "",
    borderColor: null,
    borderImageId: null,
    extraPages: [],
    monthDivider: null,
    ownerPage: initialOwnerPage,
    isTheme: false,
    calendarType: CalendarType.Calendar,
    customNumberOfEndPages: A5CalendarNumberOfExtraPages,
    coverImageId: null,
    customCoverImageId: null,
    coverColor: "rgb(171, 224, 245)",
    backCoverImageId: null,
    customBackImageId: null,
    backCoverColor: "rgb(171, 224, 245)",
    spiral: "2003",
    rubberband: "3002",
    coverTexts: [DefaultCoverTextCalendarA5],
    fontId: null,
    fontSetName: null,
};

export const reducer: Reducer<CalendarState, any> = (
    state: CalendarState = initialState,
    action: CalendarAction,
) => {
    if (!action) {
        return state;
    }

    switch (action.type) {
        case ActionType.UpdateStartDate: {
            return {
                ...state,
                startDate: action.startDate,
            };
        }
        case ActionType.UpdateLayoutId: {
            return {
                ...state,
                layoutId: action.layoutId,
            };
        }
        case ActionType.UpdatePersonalDateItems: {
            return {
                ...state,
                personalDateItems: action.personalDateItems,
            };
        }
        case ActionType.AddWeekModule:
            return {
                ...state,
                weekModules: [
                    ...state.weekModules.filter(
                        (m) => m.slotId !== action.module.slotId,
                    ),
                    action.module,
                ],
            };
        case ActionType.RemoveWeekModule:
            return {
                ...state,
                weekModules: state.weekModules.filter(
                    (m) => m.slotId !== action.slotId,
                ),
            };
        case ActionType.AddDayModule:
            return {
                ...state,
                dayModules: [
                    ...state.dayModules.filter(
                        (m) => m.slotId !== action.module.slotId,
                    ),
                    action.module,
                ],
            };
        case ActionType.RemoveDayModule:
            return {
                ...state,
                dayModules: state.dayModules.filter(
                    (m) => m.slotId !== action.slotId,
                ),
            };
        case ActionType.ChangeCalendar:
            if (
                action.changeCalendarData &&
                action.changeCalendarData.calendar
            ) {
                const {
                    calendar,
                    loadedFromLocalStorage,
                } = action.changeCalendarData;

                return {
                    ...state,
                    ...calendar,
                    calendarSize: calendar.calendarSize || state.calendarSize,
                    calendarType: calendar.calendarType || state.calendarType,
                    startDate: loadedFromLocalStorage
                        ? calendar.startDate
                        : state.startDate,
                    culture: state.culture,
                    ownerPage: calendar.ownerPage,
                };
            }

            const newState = {
                ...state,
                ...initialState,
                calendarSize: state.calendarSize,
                calendarType: state.calendarType,
                culture: state.culture,
                startDate: state.startDate,
                personalDateItems: state.personalDateItems,
                ownerPage: state.ownerPage,
            };
            return newState;
        case ActionType.UpdateBorderColor:
            return {
                ...state,
                borderColor: action.color,
            };

        case ActionType.UpdateBorderImageId:
            return {
                ...state,
                borderImageId: action.borderImageId,
            };
        case ActionType.UpdateCoverTextCollection:
            return {
                ...state,
                coverTexts: action.collection,
            };
        case ActionType.UpdateEndPageCollection:
            return {
                ...state,
                endPages: action.value,
            };
        case ActionType.UpdateLines:
            return {
                ...state,
                lines: action.value,
            };
        case ActionType.UpdateOptionalDaySettings:
            return {
                ...state,
                optionalDaySettings: action.value,
            };
        case ActionType.UpdateCalendarSize:
            return {
                ...state,
                calendarSize: action.value,
            };
        case ActionType.UpdateCalendarCulture:
            return {
                ...state,
                culture: action.value,
            };
        case ActionType.UpdateExtraPageCollection:
            return {
                ...state,
                extraPages: action.value,
            };
        case ActionType.RemoveExtraPageFromCollection:
            const index = state.extraPages.indexOf(
                state.extraPages[action.index],
            );

            return {
                ...state,
                extraPages: [
                    ...state.extraPages.slice(0, index),
                    ...state.extraPages.slice(index + 1),
                ],
            };
        case ActionType.UpdateMonthDivider:
            const calendarNumberOfExtraPages = getCalendarMaxNumberOfExtraPages(
                state.calendarType,
                state.calendarSize,
            );

            return {
                ...state,
                monthDivider: action.monthDivider,
                customNumberOfEndPages:
                    state.calendarType === CalendarType.Calendar &&
                    action.monthDivider !== null
                        ? calendarNumberOfExtraPages - MonthDividerNumberOfPages
                        : calendarNumberOfExtraPages,
            };
        case ActionType.UpdateOwnerPage:
            return {
                ...state,
                ownerPage: action.ownerPage,
            };
        case ActionType.UpdateIsTheme:
            return {
                ...state,
                isTheme: action.isTheme,
            };
        case ActionType.UpdateCalendarType:
            return {
                ...state,
                calendarType: action.calendarType,
            };
        case ActionType.UpdateCustomNumberOfEndPages:
            return {
                ...state,
                customNumberOfEndPages: action.customNumberOfEndPages,
            };
        case ActionType.UpdateCoverImageId:
            return {
                ...state,
                coverImageId: action.imageId,
            };
        case ActionType.UpdateCustomCoverImageId:
            return {
                ...state,
                customCoverImageId: action.imageId,
            };
        case ActionType.UpdateCoverColor:
            return {
                ...state,
                coverColor: action.color,
            };
        case ActionType.UpdateBackCoverImageId:
            return {
                ...state,
                backCoverImageId: action.imageId,
            };
        case ActionType.UpdateCustomBackImageId:
            return {
                ...state,
                customBackImageId: action.imageId,
            };
        case ActionType.UpdateBackCoverColor:
            return {
                ...state,
                backCoverColor: action.color,
            };
        case ActionType.UpdateSpiral:
            return {
                ...state,
                spiral: action.spiral,
            };
        case ActionType.UpdateRubberband:
            return {
                ...state,
                rubberband: action.rubberband,
            };
        case ActionType.UpdateFontId:
            return {
                ...state,
                fontSetName: null,
                fontId: action.fontId,
            };
        default:
    }

    return state || initialState;
};
